import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import FindReportAdvanceSearchForm from "./FindReportAdvanceSearchForm";

export default function FindReportAdvanceFormLayout({
  showModal,
  setSearchResult,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const [requestList, setRequestList] = useState([
    {
      requestId: 1,
      materialId: "",
      type: "",
      styleId: "",
      gsm: "",
      description: "",
      minQty: "",
      color: "",
    },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  /*   All Functions
   ********************************************* */
  const nextRequest = () => {
    if (requestList.length > 0 && currentIndex < requestList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const previousRequest = () => {
    if (requestList.length > 0 && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  const createNewRequest = () => {
    // Get highest requestId
    const maxId = requestList.reduce(
      (max, item) => Math.max(max, item.requestId),
      0
    );

    // Create a new empty request with incremented ID
    const newRequest = {
      requestId: maxId + 1,

      materialId: "",
      type: "",
      styleId: "",
      gsm: "",
      description: "",
      minQty: "",
      color: "",
    };

    // Add to list and set as current
    const updatedList = [...requestList, newRequest];
    setRequestList(updatedList);
    setCurrentIndex(updatedList.length - 1);
  };

  const duplicateRequest = () => {
    if (requestList.length === 0) return;

    // Get highest requestId
    const maxId = requestList.reduce(
      (max, item) => Math.max(max, item.requestId),
      0
    );

    // Create a duplicate with incremented ID
    const currentRequest = requestList[currentIndex];
    const duplicatedRequest = {
      ...currentRequest,
      requestId: maxId + 1,
    };

    // Add to list and set as current
    const updatedList = [...requestList, duplicatedRequest];
    setRequestList(updatedList);
    setCurrentIndex(updatedList.length - 1);

    // notify user
    dispatch(
      setNotification({
        message: "Request duplicated successfully",
        type: "success",
      })
    );
  };

  const deleteRequest = () => {
    if (requestList.length <= 1) {
      // Don't delete the last request
      dispatch(
        setNotification({
          message: "Cannot delete the last request",
          type: "warning",
        })
      );
      return;
    }

    const updatedList = requestList.filter(
      (_, index) => index !== currentIndex
    );
    setRequestList(updatedList);

    // Adjust current index if needed
    if (currentIndex >= updatedList.length) {
      setCurrentIndex(updatedList.length - 1);
    }
    // notify user
    dispatch(
      setNotification({
        message: "Request deleted successfully",
        type: "success",
      })
    );
  };

  const saveRequest = (fieldName, fieldValue) => {
    if (requestList.length === 0) return;

    // Update the current request with formData
    const updatedList = [...requestList];
    updatedList[currentIndex] = {
      ...formData,
      [fieldName]: fieldValue,
      requestId: requestList[currentIndex].requestId,
    };
    setFormData(updatedList[currentIndex]);
    setRequestList(updatedList);
  };

  const clearCurrentRequest = () => {
    if (requestList.length === 0) return;

    // Create a new empty request with the same ID
    const clearedRequest = {
      requestId: requestList[currentIndex].requestId,

      materialId: "",
      type: "",
      styleId: "",
      gsm: "",
      description: "",
      minQty: "",
      color: "",
    };

    // Update the current request
    const updatedList = [...requestList];
    updatedList[currentIndex] = clearedRequest;
    setRequestList(updatedList);
    setFormData(clearedRequest);

    // Notify user
    dispatch(
      setNotification({
        message: "Request cleared successfully",
        type: "success",
      })
    );
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer-material-lists/advanced-search",
        requestList,
        {
          headers: authHeader(),
        }
      );

      setSearchResult(data);
      setLoading(false);
      showModal(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Update formData when current index changes
  useEffect(() => {
    if (
      requestList.length > 0 &&
      currentIndex >= 0 &&
      currentIndex < requestList.length
    ) {
      setFormData(requestList[currentIndex]);
    }
  }, [currentIndex, requestList]);

  /*   All UseEffects
   ********************************************* */
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center gap-1">
        <h3 className="mt-0">Build A Find</h3>
        <div className="d-flex align-items-center gap-1">
          {/*   Request List Count
           ********************************************* */}
          <div className="me-3">
            Showing{" "}
            <span className="text-primary">
              {requestList?.length > 0 ? currentIndex + 1 : "0"}
            </span>{" "}
            of{" "}
            <span className="text-primary">
              {requestList?.length > 0 ? requestList.length : "0"}
            </span>{" "}
          </div>
          {/*   Crud Buttons
           ********************************************* */}
          {/* Add Icon Button */}
          <button
            onClick={createNewRequest}
            disabled={loading}
            className={"btn btn-success btn-sm p-0 px-1"}
            title="Create New Request"
          >
            <i className="mdi mdi-plus fs-3 py-0 my-0"></i>
          </button>

          {/* Duplicate Icon Button */}
          <button
            onClick={duplicateRequest}
            disabled={requestList?.length === 0 || loading}
            className={"btn btn-warning btn-sm p-0 px-1"}
            title="Duplicate Request"
          >
            <i className="mdi mdi-content-duplicate fs-3 py-0 my-0"></i>
          </button>

          {/* Clear Icon Button */}
          <button
            onClick={clearCurrentRequest}
            disabled={requestList?.length === 0 || loading}
            className={"btn btn-info btn-sm"}
            style={{
              paddingTop: "0.4rem",
              paddingBottom: "0.4rem",
              paddingRight: "0.8rem",
              paddingLeft: "0.8rem",
            }}
            title="Clear Current Request"
          >
            <span className="fs-5 fw-bold">C</span>
          </button>

          {/* Delete Icon Button */}
          <button
            onClick={deleteRequest}
            disabled={requestList?.length <= 1 || loading}
            className={"btn btn-danger btn-sm p-0 px-1"}
            title="Delete Request"
          >
            <i className="mdi mdi-delete fs-3 py-0 my-0"></i>
          </button>

          {/*   Border Separator
           ********************************************* */}
          <div
            className="border-start  border-3 mx-2 "
            style={{ height: "35px", width: "1px" }}
          ></div>

          {/*      Navigation Buttons
           ********************************************* */}
          <div className="d-flex align-items-center gap-1  ">
            <button
              onClick={previousRequest}
              disabled={currentIndex <= 0 || loading}
              className={"btn btn-success btn-sm p-0 px-1 "}
              title="Previous Request"
            >
              <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
            </button>
            <button
              onClick={nextRequest}
              disabled={currentIndex >= requestList.length - 1 || loading}
              className={"btn btn-success btn-sm p-0  px-1"}
              title="Next Request"
            >
              <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
            </button>
          </div>
        </div>
      </div>
      <hr className="mt-1" />

      {/*   Search Form
       ********************************************* */}
      {requestList.length > 0 && (
        <div className="">
          <FindReportAdvanceSearchForm
            formData={formData}
            setFormData={setFormData}
            loading={loading}
            setLoading={setLoading}
            showModal={showModal}
            setSearchResult={setSearchResult}
            saveRequest={saveRequest}
            handleSearch={handleSearch}
          />
        </div>
      )}
    </div>
  );
}
